import React, { useEffect, useState } from 'react';
import { fmtMoney, fmtDay } from '../lib/format';
import SwipeToDelete from './SwipeToDelete';
import { clientWhere } from '../lib/docAddress';

// ═══════════════════════════════════════════════════════════════════════════
//  ESTIMATE / INVOICE SHEET
//
//  One layout, both documents. An estimate and an invoice are the same object
//  at two moments in its life, and drawing them from one component is what
//  stops them drifting into two different-looking things.
//
//  ── WHAT IS DELIBERATELY NOT HERE ────────────────────────────────────────
//  A tax line, and a "Total Due". This is the VENDOR'S copy, and the number on
//  it is what QPS owes them — not what the homeowner pays. QPS adds its markup
//  on top, and the server strips that markup out before an estimate is ever
//  sent back to a tech. A "Total Due" here would teach a vendor that their
//  number is the customer's number, which is the one thing this platform
//  cannot let them believe. Their sheet ends at their own total.
//
//  ── BULLETS ──────────────────────────────────────────────────────────────
//  A line's description is plain text, and every newline in it draws as a
//  bullet. That is the whole mechanism: a tech writes one job per line in the
//  editor and gets the list from the mockup, with no new field to fill in and
//  nothing to learn. A line starting with "Note:" sets in italic underneath,
//  which is how "homeowner to provide vanities" reads on paper.
// ═══════════════════════════════════════════════════════════════════════════

const NOTE = /^\s*(note|nota)\s*:\s*/i;

function Lines({ description }) {
  const rows = String(description || '').split('\n').map(r => r.trim()).filter(Boolean);
  if (rows.length === 0) return null;
  const bullets = rows.filter(r => !NOTE.test(r));
  const notes = rows.filter(r => NOTE.test(r)).map(r => r.replace(NOTE, ''));
  return (
    <>
      {bullets.length > 0 && (
        <ul className="qd-bullets">
          {bullets.map((r, i) => <li key={i}>{r}</li>)}
        </ul>
      )}
      {notes.map((r, i) => <p key={i} className="qd-note">{r}</p>)}
    </>
  );
}

// What is attached to a line, at a glance. A tech who photographed a burst
// pipe and attached the inspection report should be able to see from the list
// that both made it, without opening the line to check.
const FileCount = ({ n }) => (
  <span className="qd-shots" title={`${n} file${n === 1 ? '' : 's'}`}>
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 3.5H6.5A1.5 1.5 0 005 5v14a1.5 1.5 0 001.5 1.5h11A1.5 1.5 0 0019 19V8.5z" />
      <path d="M14 3.5V9h5" />
    </svg>
    ({n})
  </span>
);

const PhotoCount = ({ n }) => (
  <span className="qd-shots" title={`${n} photo${n === 1 ? '' : 's'}`}>
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <circle cx="8.5" cy="10" r="1.6" />
      <path d="M21 16l-5-5-5 5-2.5-2.5L3 18" />
    </svg>
    ({n})
  </span>
);

export default function DocSheet({
  kind,            // 'estimate' | 'invoice'
  title,           // "Edit Estimate"
  number,          // "#1042"
  date,            // when it was created — today on one that has never been saved
  address,         // the street line, "1234 E Oak Ridge Dr"
  city, state, zip,
  cityLine,        // what is shown under it when the three above are empty
  items = [],
  total,
  onOpenItem,
  onAddItem,
  onDeleteItem,
  onAddressChange,   // ({ address, city, state, zip })
  onDateChange,      // (yyyy-mm-dd) — omitted makes the date read-only
  onNumberChange,    // (string) — omitted makes the number read-only
  // ── WHO IT IS FOR ───────────────────────────────────────────────────────
  // A document with no name on it is not a document, it is a price list. Until
  // now a technician was asked who their estimate was for at the moment they
  // SENT it, in the sending sheet, and nowhere else — so there was no way to
  // write the owner's name down while sitting in their driveway, and no way to
  // correct it afterwards.
  //
  // `clientFixed` is for a job QPS dispatched: that estimate is to QPS, not to
  // the homeowner, and letting a technician type a homeowner's name on it
  // would be teaching them the wrong thing about whose customer it is.
  client,            // { name, phone, email, … } | null
  clientFixed,       // a string — shown instead, with nothing to edit
  onEditClient,      // opens the client screen; omitted means no row at all
  onCancel,
  onDone,                        // save — keeps the sheet open
  doneLabel = 'Done',
  onSend,                        // send — hands the document on
  sendLabel = 'Send',
  busy,
  children,        // notes, warnings — anything above the totals
}) {
  const label = kind === 'invoice' ? 'Invoice' : 'Estimate';
  // The address lives in the banner because that is where it is read. Editing
  // it anywhere else means a tech scrolling past the thing they are trying to
  // correct to reach a field that repeats it.
  // Four boxes on their own sheet, not one input wedged into the banner. That
  // box was about 150px wide on a phone, so an address longer than a few words
  // scrolled sideways inside it and a tech was editing something they could
  // not read. Big labelled fields, one thing per line, the way it would be
  // written on paper.
  const [editingAddress, setEditingAddress] = useState(false);
  const [draft, setDraft] = useState({ address: '', city: '', state: '', zip: '' });

  const openAddress = () => {
    setDraft({ address: address || '', city: city || '', state: state || '', zip: zip || '' });
    setEditingAddress(true);
  };

  // Which of the two buttons is waiting. Both call the same save underneath,
  // so without this the corner reads "Saving…" while a tech is sending, and
  // the send button reads nothing at all.
  const [pending, setPending] = useState(null);
  useEffect(() => { if (!busy) setPending(null); }, [busy]);
  const run = (which, fn) => () => { setPending(which); if (fn) fn(); };

  const saveAddress = () => {
    setEditingAddress(false);
    if (onAddressChange) {
      onAddressChange({
        address: draft.address.trim(),
        city: draft.city.trim(),
        state: draft.state.trim(),
        zip: draft.zip.trim(),
      });
    }
  };
  const setField = (k) => (e) => setDraft(p => ({ ...p, [k]: e.target.value }));

  // What <input type="date"> needs: yyyy-mm-dd, in the LOCAL day. Reading it
  // out of toISOString() would be the UTC day, which west of UTC is yesterday
  // for the whole evening — a technician dating an invoice at 6pm would watch
  // the field say the day before.
  const dateInput = (() => {
    const d = date ? new Date(date) : new Date();
    if (isNaN(d)) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();

  // "Alpine, UT 84004" — a comma after the city, a space before the ZIP. An
  // older record has all of it on the street line and none of these, and falls
  // back to whatever cityLine was passed.
  const below = [
    [city, state].filter(Boolean).join(', ') + (zip ? ` ${zip}` : ''),
  ].map(x => x.trim()).filter(Boolean).join('') || cityLine;

  // Where the client is, on one line under their name. This is the address
  // the work is at: written once, in the client, and the document's own
  // serviceAddress is filled from it when the client is saved.
  const where = clientWhere(client);

  // The address row at the top earns its place only when there is no client
  // block to carry it: a job QPS dispatched, where the customer is QPS and
  // the property is somewhere else entirely.
  const showAddress = Boolean(clientFixed) || !onEditClient;

  return (
    <div className="qd">
      <div className="qd-bar">
        <button type="button" className="qd-bar-btn" onClick={onCancel}>Cancel</button>
        <span className="qd-bar-title">{title}</span>
        {/* Only drawn when there is something for it to do. An invoice has no
            draft to save, so it carries one action at the bottom and nothing
            in the corner — a "Done" that does nothing is worse than a gap. */}
        {onDone ? (
          <button type="button" className="qd-bar-btn strong" onClick={run('save', onDone)} disabled={busy}>
            {busy && pending === 'save' ? 'Saving…' : doneLabel}
          </button>
        ) : <span className="qd-bar-btn" aria-hidden="true" />}
      </div>

      <div className="qd-body">

        {/* ── Who it is for ─────────────────────────────────────────────
            First, above everything. It used to sit under the service address,
            so the first thing on a technician's estimate was a street and the
            second was the person — and the street was typed twice, because it
            is already part of the client. Name, where they are, then how to
            reach them. */}
        {(clientFixed || onEditClient) && (
          <button
            type="button"
            className={`qd-client${client?.name || clientFixed ? '' : ' empty'}`}
            onClick={clientFixed ? undefined : onEditClient}
            disabled={Boolean(clientFixed)}
          >
            <span className="qd-client-l">Prepared for</span>
            <span className="qd-client-n">
              {clientFixed || client?.name || 'Who is this for?'}
            </span>
            {!clientFixed && where && <span className="qd-client-s">{where}</span>}
            {!clientFixed && (() => {
              // Their phone and email under the address, because the two
              // things a technician checks before sending are whether he has
              // a way to reach them and whether the email is the right one.
              const sub = [client?.phone, client?.email].filter(Boolean).join(' · ');
              return sub ? <span className="qd-client-s">{sub}</span> : null;
            })()}
            {!clientFixed && (
              <span className="qd-client-pencil" aria-hidden="true">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 20h4L19 9l-4-4L4 16v4z" /></svg>
              </span>
            )}
          </button>
        )}

        {/* ── The head of the document ───────────────────────────────────
            A white card, like every other block on this screen. It used to be
            a tall green banner built to hold a property photo — and most
            documents have no photo, so most of the time it was a slab of flat
            green doing nothing but pushing the work off the bottom of the
            phone.

            The service address used to sit here as well, above the customer,
            and it was the same address a second time. It is written once now,
            with the client, and only shown here on a job QPS dispatched —
            where the customer IS QPS and the property is genuinely a separate
            fact. */}
        <div className="qd-head">
          {showAddress && (
            <button
              type="button"
              className={`qd-head-row qd-head-addr${address ? '' : ' empty'}`}
              onClick={openAddress}
              aria-label={address ? `Edit address, ${address}` : 'Add an address'}
              disabled={!onAddressChange}
            >
              <span className="qd-head-l">Service address</span>
              <span className="qd-head-v">{address || 'Add an address'}</span>
              {below && <span className="qd-head-s">{below}</span>}
              {onAddressChange && (
                <span className="qd-head-pencil" aria-hidden="true">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 20h4L19 9l-4-4L4 16v4z" /></svg>
                </span>
              )}
            </button>
          )}

          <div className="qd-head-pair">
            {/* Theirs to type. Julian is at estimate 1179 and invoice 1876 in
                Joist and is carrying those sequences on here, so that an
                accountant asking for invoice 1877 next March finds exactly
                one of them. Left blank, the app takes the next number after
                whatever he last used. */}
            <label className="qd-head-cell qd-head-num">
              <span className="qd-head-l">{label} #</span>
              {onNumberChange ? (
                <input
                  type="text"
                  inputMode="text"
                  value={number || ''}
                  placeholder="Next one"
                  onChange={(e) => onNumberChange(e.target.value)}
                  aria-label={`${label} number`}
                />
              ) : (
                <span className="qd-head-v">{number || '—'}</span>
              )}
            </label>
            {/* Editable, because the date on a document is the technician's to
                set: the estimate was agreed at the kitchen table on Tuesday
                and typed up on Thursday, and the bill for a job that finished
                on the 30th goes out on the 2nd. A native date field, so every
                phone gives them the picker they already know. */}
            <label className="qd-head-cell qd-head-date">
              <span className="qd-head-l">Date</span>
              {onDateChange ? (
                <input
                  type="date"
                  value={dateInput}
                  onChange={(e) => onDateChange(e.target.value)}
                  aria-label={`Date on this ${label.toLowerCase()}`}
                />
              ) : (
                <span className="qd-head-v">{fmtDay(date || new Date())}</span>
              )}
            </label>
          </div>
        </div>

        {items.length === 0 ? (
          <div className="qd-empty">
            Nothing on this {label.toLowerCase()} yet. Add a line for each piece of
            work, with what it covers and a photo.
          </div>
        ) : items.map((it, i) => {
          const shots = (it.photos || []).length;
          const docs = (it.files || []).length;
          const amount = (parseFloat(it.qty) || 0) * (parseFloat(it.unitCost) || 0);
          const row = (
            <button type="button" className="qd-item" onClick={() => onOpenItem && onOpenItem(i)}>
              <span className={`qd-thumb${shots ? '' : ' empty'}`}
                style={shots ? { backgroundImage: `url(${it.photos[0].url || it.photos[0]})` } : undefined} />
              <span className="qd-item-main">
                <span className="qd-item-top">
                  <b>{it.name || it.description?.split('\n')[0] || 'Untitled line'}</b>
                  <em>{fmtMoney(amount)}</em>
                </span>
                {(shots > 0 || docs > 0) && (
                  <span className="qd-counts">
                    {shots > 0 && <PhotoCount n={shots} />}
                    {docs > 0 && <FileCount n={docs} />}
                  </span>
                )}
                <Lines description={it.description} />
              </span>
              <span className="qd-chev" aria-hidden="true">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7" /></svg>
              </span>
            </button>
          );
          // Swipe either way to reveal Delete. The confirm lives with the
          // caller, because deleting a line someone just photographed and
          // priced should ask first.
          return onDeleteItem
            ? <SwipeToDelete key={i} onDelete={() => onDeleteItem(i)}>{row}</SwipeToDelete>
            : <React.Fragment key={i}>{row}</React.Fragment>;
        })}

        {/* Directly under the last line, which is where the hand already is
            after adding one. It used to sit below the totals, so adding a
            second line meant scrolling past the arithmetic to get to it. */}
        {onAddItem && (
          <button type="button" className="qd-add" onClick={onAddItem}>
            <span aria-hidden="true">+</span> Add Item
          </button>
        )}

        <div className="qd-totals">
          <div className="qd-total-row">
            <span>Subtotal</span>
            <span>{fmtMoney(total)}</span>
          </div>
          {/* See the note at the top: no tax row, and the word is "total",
              not "due". This is what QPS owes the tech. */}
          <div className="qd-total-row grand">
            <span>{kind === 'invoice' ? 'Invoice total' : 'Estimate total'}</span>
            <span>{fmtMoney(total)}</span>
          </div>
        </div>

        {children}

        {/* Save sits in the corner and sending sits here, at the end of the
            document, because that is the order the work happens in: a tech
            saves half a dozen times while pricing a job, and sends once, after
            reading the whole thing back. Two buttons that look alike would get
            the second one pressed by accident. */}
        {onSend && (
          <button type="button" className="qd-send" onClick={run('send', onSend)} disabled={busy}>
            {busy && pending === 'send' ? 'Sending…' : sendLabel}
          </button>
        )}

      </div>

      {/* One thing per line, each with its own label. Nothing to scroll
          sideways in, and nothing to work out. */}
      {editingAddress && (
        <>
          <div className="qd-addr" role="dialog" aria-label="Service address">
            <div className="qd-addr-box">
              <div className="qd-addr-t">Service address</div>

              <label className="qd-addr-f">
                <span>Street address</span>
                <input autoFocus type="text" value={draft.address} onChange={setField('address')}
                  autoComplete="address-line1" placeholder="596 N Main St" />
              </label>

              <label className="qd-addr-f">
                <span>City</span>
                <input type="text" value={draft.city} onChange={setField('city')}
                  autoComplete="address-level2" placeholder="Alpine" />
              </label>

              <div className="qd-addr-row">
                <label className="qd-addr-f">
                  <span>State</span>
                  <input type="text" value={draft.state} onChange={setField('state')}
                    autoComplete="address-level1" maxLength={2} placeholder="UT" />
                </label>
                <label className="qd-addr-f">
                  <span>ZIP code</span>
                  <input type="text" inputMode="numeric" maxLength={10} value={draft.zip} onChange={setField('zip')}
                    autoComplete="postal-code" placeholder="84004" />
                </label>
              </div>

              <button type="button" className="qd-addr-done" onClick={saveAddress}>Done</button>
              <button type="button" className="qd-addr-cancel" onClick={() => setEditingAddress(false)}>Cancel</button>
            </div>
          </div>
          <div className="qd-addr-veil" onClick={() => setEditingAddress(false)} />
        </>
      )}
    </div>
  );
}
